<template>
  <VaDropdown
    v-model="showDropdown"
    placement="bottom-end"
    :offset="[0, 12]"
    stick-to-edges
    :close-on-content-click="false"
    class="notification-dropdown-wrapper admin-notifications"
  >
    <template #anchor>
      <div class="notification-bell">
        <div class="notification-bell__wrapper">
          <VaIcon :name="bellIcon" class="notification-bell__icon" />
          <span v-if="unreadCount > 0" class="notification-badge">
            {{ unreadCount > 99 ? '99+' : unreadCount }}
          </span>
        </div>
      </div>
    </template>

    <VaDropdownContent class="notification-dropdown admin-notifications-panel">
      <div class="notification-header">
        <h3>{{ t('notifications.title') }}</h3>
        <VaButton
          size="small"
          preset="plain"
          :disabled="unreadCount === 0"
          @click="markAllAsRead"
        >
          {{ t('notifications.markAllAsRead') }}
        </VaButton>
      </div>

      <div v-if="isLoadingAlerts && mergedNotifications.length === 0" class="notification-empty">
        <VaProgressCircle indeterminate size="2rem" />
        <p>{{ t('notifications.loading') }}</p>
      </div>

      <div
        v-else-if="mergedNotifications.length > 0"
        class="notification-list"
        @scroll.passive="handleNotificationListScroll"
      >
        <div
          v-for="item in mergedNotifications"
          :key="item.key"
          class="notification-item"
          :class="{
            'notification-item--unread': !item.read,
            'notification-item--active': item.kind === 'promotion' && item.data.type === 'DOMAIN_ACTIVE',
            'notification-item--dead': item.kind === 'promotion' && item.data.type !== 'DOMAIN_ACTIVE',
          }"
          @click="handleClick(item)"
        >
          <div
            class="notification-icon"
            :class="item.kind === 'promotion' && item.data.type === 'DOMAIN_ACTIVE'
              ? 'notification-icon--success'
              : 'notification-icon--error'"
          >
            <VaIcon :name="item.icon" size="18px" />
          </div>

          <div class="notification-content">
            <template v-if="item.kind === 'promotion'">
              <p class="notification-title">
                {{
                  item.data.type === 'DOMAIN_ACTIVE'
                    ? t('adminPromotionNotifications.activeLinkTitle')
                    : t('adminPromotionNotifications.deadLinkTitle')
                }}
              </p>
              <p class="notification-message">{{ item.data.domainUrl }}</p>
            </template>

            <template v-else>
              <p class="notification-title">
                {{ t(`adminPaymentErrorNotifications.${item.data.errorType}`) }}
              </p>
              <p class="notification-message">
                {{ t('adminPaymentErrorNotifications.channelLabel') }}:
                {{ formatPaymentField(item.data.channel) }}
              </p>
              <p class="notification-message">
                {{ t('adminPaymentErrorNotifications.deviceLabel') }}:
                {{ formatPaymentField(item.data.device) }}
              </p>
              <p class="notification-message">
                {{
                  t('adminPaymentErrorNotifications.countLabel', {
                    count: item.data.count,
                  })
                }}
              </p>
            </template>

            <span class="notification-time">{{ formatTime(item.timestamp) }}</span>
          </div>

          

          <div v-if="!item.read" class="notification-dot" />
        </div>

        <div v-if="isLoadingMoreAlerts" class="notification-more">
          <VaProgressCircle indeterminate size="1.5rem" />
        </div>
      </div>

      <div v-else class="notification-empty">
        <VaIcon name="notifications_none" size="3rem" />
        <p>{{ t('notifications.noNotifications') }}</p>
      </div>
    </VaDropdownContent>
  </VaDropdown>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { VaButton, VaDropdown, VaDropdownContent, VaIcon, VaProgressCircle } from 'vuestic-ui'
import {
  useAdminPaymentErrorNotifications,
  type AdminPaymentErrorNotification,
} from '@/composables/useAdminPaymentErrorNotifications'
import {
  useAdminPromotionDeadNotifications,
  type AdminPromotionDeadNotification,
} from '@/composables/useAdminPromotionDeadNotifications'

type MergedNotification =
  | {
      kind: 'promotion'
      key: string
      icon: 'check_circle' | 'error_outline'
      timestamp: string
      read: boolean
      data: AdminPromotionDeadNotification
    }
  | {
      kind: 'paymentError'
      key: string
      icon: 'error_outline'
      timestamp: string
      read: boolean
      data: AdminPaymentErrorNotification
    }

const emit = defineEmits<{
  notify: [payload: {
    type: 'payment' | 'promotion'
    message: string
    domainStatus?: 'DOMAIN_DEAD' | 'DOMAIN_ACTIVE'
  }]
  toggleError: []
}>()

const { t } = useI18n()
const router = useRouter()
const showDropdown = ref(false)

const {
  notifications: paymentNotifications,
  unreadCount: paymentUnreadCount,
  initializePaymentErrorNotifications,
  cleanupPaymentErrorNotifications,
  markAsRead: markPaymentAsRead,
  markAllAsRead: markAllPaymentAsRead,
} = useAdminPaymentErrorNotifications()

const {
  notifications: promotionNotifications,
  unreadCount: promotionUnreadCount,
  togglingIds: promotionTogglingIds,
  hasMore: hasMoreAlerts,
  isLoading: isLoadingAlerts,
  isLoadingMore: isLoadingMoreAlerts,
  initializePromotionDeadNotifications,
  cleanupPromotionDeadNotifications,
  loadMoreAlerts,
  markAsRead: markPromotionAsRead,
  markAllAsRead: markAllPromotionAsRead,
  toggleNotificationEnable,
} = useAdminPromotionDeadNotifications()

const unreadCount = computed(() => paymentUnreadCount.value + promotionUnreadCount.value)

const bellIcon = computed(() => (unreadCount.value > 0 ? 'notifications_active' : 'notifications'))

const mergedNotifications = computed<MergedNotification[]>(() => {
  const items: MergedNotification[] = [
    ...promotionNotifications.value.map((notification) => ({
      kind: 'promotion' as const,
      key: `promotion-${notification.id}`,
      icon: (notification.type === 'DOMAIN_ACTIVE' ? 'check_circle' : 'error_outline') as 'check_circle' | 'error_outline',
      timestamp: notification.timestamp,
      read: notification.read,
      data: notification,
    })),
    ...paymentNotifications.value.map((notification) => ({
      kind: 'paymentError' as const,
      key: `payment-${notification.id}`,
      icon: 'error_outline' as const,
      timestamp: notification.timestamp,
      read: notification.read,
      data: notification,
    })),
  ]

  return items.sort(
    (left, right) => new Date(right.timestamp).getTime() - new Date(left.timestamp).getTime(),
  )
})

const formatPaymentField = (value: string | null) =>
  value || t('adminPaymentErrorNotifications.notApplicable')

const formatTime = (dateString: string): string => {
  const date = new Date(dateString)
  const diff = Date.now() - date.getTime()
  const minutes = Math.floor(diff / 60000)
  const hours = Math.floor(diff / 3600000)
  const days = Math.floor(diff / 86400000)

  if (minutes < 1) return t('notifications.atTheInstant')
  if (minutes < 60) return t('notifications.minutesAgo').replace('{count}', minutes.toString())
  if (hours < 24) return t('notifications.hoursAgo').replace('{count}', hours.toString())
  if (days === 1) return t('notifications.yesterday')
  if (days < 7) return t('notifications.daysAgo').replace('{count}', days.toString())

  return date.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const buildPaymentMessage = (notification: AdminPaymentErrorNotification) =>
  t('adminPaymentErrorNotifications.summaryMessage', {
    type: t(`adminPaymentErrorNotifications.${notification.errorType}`),
    channel: formatPaymentField(notification.channel),
    device: formatPaymentField(notification.device),
    count: notification.count,
  })

const handleNotificationListScroll = (event: Event) => {
  const container = event.currentTarget as HTMLElement | null
  if (
    !container ||
    isLoadingAlerts.value ||
    isLoadingMoreAlerts.value ||
    !hasMoreAlerts.value
  ) {
    return
  }

  const distanceToBottom = container.scrollHeight - (container.scrollTop + container.clientHeight)
  if (distanceToBottom <= 120) {
    void loadMoreAlerts()
  }
}

const markAllAsRead = () => {
  markAllPromotionAsRead()
  markAllPaymentAsRead()
}

const normalizeDomainUrl = (domainUrl: string) => {
  const url = domainUrl.trim()
  if (!url) return ''
  if (/^https?:\/\//i.test(url)) return url
  return `https://${url}`
}

const handleClick = async (item: MergedNotification) => {
  if (item.kind === 'promotion') {
    markPromotionAsRead(item.data.id)

    const url = normalizeDomainUrl(item.data.domainUrl)
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
    return
  }

  markPaymentAsRead(item.data.id)
  showDropdown.value = false
  await router.push({ name: 'orders' })
}

const handleToggleEnable = async (notification: AdminPromotionDeadNotification) => {
  try {
    const nextEnable = await toggleNotificationEnable(notification)
    if (nextEnable === null) return

    markPromotionAsRead(notification.id)
    emit('notify', {
      type: 'promotion',
      message:
        nextEnable === 1
          ? t('adminPromotionNotifications.enabledSuccess', { promotionId: notification.promotionId })
          : t('adminPromotionNotifications.disabledSuccess', { promotionId: notification.promotionId }),
    })
  } catch {
    emit('toggleError')
  }
}

watch(
  () => mergedNotifications.value[0]?.key,
  (latestKey, previousKey) => {
    if (previousKey === undefined) return
    if (!latestKey || latestKey === previousKey) return

    const latest = mergedNotifications.value[0]
    if (!latest) return

    if (latest.kind === 'promotion') {
      emit('notify', {
        type: 'promotion',
        message: latest.data.domainUrl,
        domainStatus: latest.data.type,
      })
      return
    }

    emit('notify', {
      type: 'payment',
      message: buildPaymentMessage(latest.data),
    })
  },
)

onMounted(async () => {
  await Promise.all([
    initializePromotionDeadNotifications(),
    initializePaymentErrorNotifications(),
  ])
})

onUnmounted(() => {
  cleanupPromotionDeadNotifications()
  cleanupPaymentErrorNotifications()
})
</script>

<style scoped>
.admin-notifications-panel :deep(.notification-icon) {
  width: 36px;
  height: 36px;
  border-radius: 12px;
  margin-right: 0.75rem;
}

.notification-item--dead::before {
  background: #e23d3d !important;
}

.notification-item--active::before {
  background: #22a45a !important;
}

.admin-notifications-panel :deep(.notification-icon--error) {
  background: #fdecee !important;
  color: #e23d3d !important;
  box-shadow: 0 6px 14px rgba(226, 61, 61, 0.14);
}

.admin-notifications-panel :deep(.notification-icon--success) {
  background: #e8f8ee !important;
  color: #22a45a !important;
  box-shadow: 0 6px 14px rgba(34, 164, 90, 0.14);
}

.notification-more {
  display: flex;
  justify-content: center;
  padding: 0.75rem 0;
}

.notification-item__toggle {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  min-width: 72px;
}

.notification-item__toggle-label {
  font-size: 11px;
  color: var(--va-secondary);
  text-align: center;
  line-height: 1.2;
}
</style>
